import React, { memo } from 'react';
import { FileTextIcon, BookOpenIcon, ArrowUpRightIcon, LinkedinIcon } from 'lucide-react';
export const ResourcesSection = () => {
  const resources = [{
    icon: <FileTextIcon size={24} data-id="element-443" />,
    title: 'The Ultimate Guide to Sponsorship Valuation',
    description: 'Learn how to accurately value your sponsorship assets to maximize revenue',
    type: 'Whitepaper',
    cta: 'Download'
  }, {
    icon: <BookOpenIcon size={24} data-id="element-444" />,
    title: '7 Strategies for Sponsor Retention',
    description: 'Proven tactics to keep sponsors coming back year after year',
    type: 'E-Book',
    cta: 'Download'
  }, {
    icon: <FileTextIcon size={24} data-id="element-445" />,
    title: 'Sponsorship Trends for 2023',
    description: 'The latest trends shaping the sponsorship landscape',
    type: 'Report',
    cta: 'Download'
  }];
  const articles = [{
    title: 'How to Create Sponsorship Packages That Sell',
    excerpt: 'Learn the key elements of high-converting sponsorship packages that attract premium sponsors.',
    readTime: '7 min read'
  }, {
    title: 'The ROI of Outsourced Sponsorship Sales',
    excerpt: 'A data-driven analysis of the financial benefits of outsourcing your sponsorship sales function.',
    readTime: '5 min read'
  }, {
    title: 'Maximizing Sponsor Activation: Best Practices',
    excerpt: 'Practical strategies to help sponsors maximize their ROI through effective activations.',
    readTime: '6 min read'
  }, {
    title: 'Digital Sponsorship Opportunities for Virtual Events',
    excerpt: 'Innovative ways to create valuable digital sponsorship opportunities in virtual environments.',
    readTime: '8 min read'
  }];
  return <section id="resources" className="py-16 md:py-24 bg-[#f5f5f5]" data-id="element-446">
      <div className="container mx-auto px-6" data-id="element-447">
        <div className="mb-5" data-id="element-448">
          <span className="text-[#1B2A4A] font-medium inline-flex items-center gap-2 bg-white/70 px-4 py-1.5 rounded-full shadow-sm" data-id="element-449">
            <div className="w-6 h-6 bg-[#B87333] rounded-sm" data-id="element-450"></div>
            Resources
          </span>
        </div>
        <div className="mb-10 md:mb-16" data-id="element-451">
          <h2 className="text-3xl md:text-5xl font-bold text-[#1B2A4A] mb-6" data-id="element-452">
            Resources &{' '}
            <span className="font-serif italic text-[#B87333]" data-id="element-453">Insights</span>
          </h2>
          <p className="text-lg md:text-xl text-[#1B2A4A]/80 max-w-2xl leading-relaxed" data-id="element-454">
            Expert guides, research, and thought leadership to help you maximize
            your sponsorship potential
          </p>
        </div>

        {/* Featured Resource with Image */}
        <div className="mb-12 md:mb-20 bg-[#1B2A4A] rounded-lg overflow-hidden shadow-lg" data-id="element-455">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-0" data-id="element-456">
            <div className="p-6 md:p-10 flex flex-col justify-center" data-id="element-457">
              <span className="bg-[#B87333]/30 text-[#B87333] px-4 py-1.5 rounded-full text-sm font-medium inline-block mb-5 w-fit" data-id="element-458">
                Featured Guide
              </span>
              <h3 className="text-2xl font-bold mb-5 text-white" data-id="element-459">
                Sponsorship Activation Playbook
              </h3>
              <p className="text-white/80 mb-8 leading-relaxed" data-id="element-460">
                Learn how to create memorable sponsor activations that drive
                engagement, generate buzz, and deliver measurable ROI for your
                sponsors.
              </p>
              <a href="#" className="inline-flex items-center gap-2 bg-[#B87333] hover:bg-[#B87333]/90 text-[#1B2A4A] px-5 py-3 rounded-md transition-all w-fit shadow-md font-medium" data-id="element-461">
                Download Free Guide <ArrowUpRightIcon size={18} data-id="element-462" />
              </a>
            </div>
            <div className="h-full min-h-[300px] md:min-h-0" data-id="element-463">
              <img src="https://uploadthingy.s3.us-west-1.amazonaws.com/1zXnLN35S3eeviMBLvikK9/Lift.jpg" alt="Conference attendees in elevator" className="w-full h-full object-cover" data-id="element-464" />
            </div>
          </div>
        </div>

        {/* Blog Articles */}
        <div className="bg-white rounded-lg p-6 md:p-10 border border-gray-100 hover:shadow-lg transition-all shadow-md mb-12 md:mb-20" data-id="element-465">
          <div className="flex justify-between items-center mb-6 md:mb-10" data-id="element-466">
            <h3 className="text-2xl font-bold text-[#1B2A4A]" data-id="element-467">
              Latest Articles
            </h3>
            <a href="#" className="text-[#B87333] font-medium hover:text-[#B87333]/80 transition-colors inline-flex items-center gap-2" data-id="element-468">
              View All <ArrowUpRightIcon size={18} data-id="element-469" />
            </a>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8" data-id="element-470">
            {articles.map((article, index) => <div key={index} className="bg-[#f5f5f5] p-5 md:p-7 rounded-lg border border-gray-100 shadow-sm hover:bg-[#f5f5f5]/80 hover:shadow-md transition-all h-full flex flex-col transform hover:-translate-y-1 duration-300" data-id="element-471">
                <h4 className="text-xl font-bold text-[#1B2A4A] mb-4" data-id="element-472">
                  {article.title}
                </h4>
                <p className="text-[#1B2A4A]/70 mb-6 flex-grow leading-relaxed" data-id="element-473">
                  {article.excerpt}
                </p>
                <div className="flex justify-between items-center mt-auto" data-id="element-474">
                  <span className="text-[#1B2A4A]/50 text-sm" data-id="element-475">
                    {article.readTime}
                  </span>
                  <a href="#" className="text-[#B87333] font-medium hover:text-[#B87333]/80 transition-colors inline-flex items-center gap-2" data-id="element-476">
                    Read Article <ArrowUpRightIcon size={18} data-id="element-477" />
                  </a>
                </div>
              </div>)}
          </div>
        </div>

        {/* LinkedIn Follow */}
        <div className="mt-10 md:mt-16 bg-[#1B2A4A] text-white rounded-lg p-6 md:p-10 shadow-lg hover:shadow-xl transition-all transform hover:-translate-y-1 duration-300" data-id="element-478">
          <div className="md:flex items-center justify-between" data-id="element-479">
            <div className="mb-8 md:mb-0 md:mr-10" data-id="element-480">
              <h3 className="text-2xl font-bold mb-3" data-id="element-481">Follow us on LinkedIn</h3>
              <p className="text-white/80 leading-relaxed" data-id="element-482">
                Stay connected with our latest insights, success stories, and
                sponsorship opportunities.
              </p>
            </div>
            <div className="flex-shrink-0 w-full md:w-auto" data-id="element-483">
              <a href="https://www.linkedin.com/company/sales-source/" target="_blank" rel="noopener noreferrer" className="bg-[#B87333] text-[#1B2A4A] hover:bg-[#B87333]/90 font-medium px-6 py-3 rounded-md transition-colors whitespace-nowrap border border-[#B87333] shadow-md flex items-center justify-center gap-2 w-full" data-id="element-484">
                <LinkedinIcon size={20} data-id="element-485" />
                Follow on LinkedIn
              </a>
            </div>
          </div>
        </div>
      </div>
    </section>;
};